/**
 * Upload AI Defaults Cookie Helper
 *
 * Persists the three "use AI for title/description/tags" toggles from the
 * Upload Content page so a returning user lands on their last-used defaults.
 *
 * Storage: small JSON object with short keys (t, d, g) in a long-lived
 * SameSite=Lax cookie. No server roundtrip.
 */

export interface UploadAiDefaults {
  aiTitle: boolean;
  aiDescription: boolean;
  aiTags: boolean;
}

export const DEFAULT_UPLOAD_AI_DEFAULTS: UploadAiDefaults = {
  aiTitle: true,
  aiDescription: true,
  aiTags: true,
};

export const UPLOAD_AI_DEFAULTS_COOKIE = 'sharp_upload_ai_defaults';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

const readRawCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match?.split('=').slice(1).join('=');
};

export const readUploadAiDefaults = (): UploadAiDefaults => {
  const raw = readRawCookie(UPLOAD_AI_DEFAULTS_COOKIE);
  if (!raw) return DEFAULT_UPLOAD_AI_DEFAULTS;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<{
      t: 0 | 1;
      d: 0 | 1;
      g: 0 | 1;
    }>;
    return {
      aiTitle: parsed.t === 0 ? false : true,
      aiDescription: parsed.d === 0 ? false : true,
      aiTags: parsed.g === 0 ? false : true,
    };
  } catch {
    return DEFAULT_UPLOAD_AI_DEFAULTS;
  }
};

export const writeUploadAiDefaults = (value: UploadAiDefaults): void => {
  if (typeof document === 'undefined') return;
  const packed = {
    t: value.aiTitle ? 1 : 0,
    d: value.aiDescription ? 1 : 0,
    g: value.aiTags ? 1 : 0,
  };
  const encoded = encodeURIComponent(JSON.stringify(packed));
  document.cookie = `${UPLOAD_AI_DEFAULTS_COOKIE}=${encoded}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
};
